<script setup>
import { ref, computed, watch } from 'vue'
import { useStorage } from '@vueuse/core'
import { useToast } from 'primevue/usetoast'
import { useRoute } from 'vue-router'
import api from '@/lib/api'

const route = useRoute()
const toast = useToast()
const currentEnvironment = useStorage('currentEnvironment', 'default')

const loading = ref(false)
const saving = ref(false)
const tables = ref([])
const exposedSet = ref(new Set())
const search = ref('')
const collapsedSchemas = ref(new Set())

const odataBaseUrl = computed(() => `${window.location.origin}/odata/${currentEnvironment.value}`)
const serviceDocumentUrl = computed(() => odataBaseUrl.value)
const metadataUrl = computed(() => `${odataBaseUrl.value}/$metadata`)

const tableKey = (schema, tableName) => `${schema}__${tableName}`

// Group tables by schema, applying search filter
const tablesBySchema = computed(() => {
  const q = search.value.trim().toLowerCase()
  const groups = {}
  for (const t of tables.value) {
    if (q && !t.tableName.toLowerCase().includes(q) && !t.schema.toLowerCase().includes(q)) continue
    if (!groups[t.schema]) groups[t.schema] = []
    groups[t.schema].push(t.tableName)
  }
  return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b))
})

const isCollapsed = (schema) => collapsedSchemas.value.has(schema)
const toggleSchema = (schema) => {
  const updated = new Set(collapsedSchemas.value)
  if (updated.has(schema)) updated.delete(schema)
  else updated.add(schema)
  collapsedSchemas.value = updated
}
const collapseAll = () => {
  collapsedSchemas.value = new Set(tablesBySchema.value.map(([s]) => s))
}
const expandAll = () => {
  collapsedSchemas.value = new Set()
}

const fetchData = async () => {
  loading.value = true
  try {
    const [tablesRes, configRes] = await Promise.all([
      api.odata.getTables(),
      api.odata.getConfig()
    ])
    tables.value = tablesRes.data || []
    exposedSet.value = new Set(
      (configRes.data?.exposedTables || []).map(t => tableKey(t.schema, t.tableName))
    )
  } catch (e) {
    toast.add({ severity: 'error', group: 'tr', summary: 'Error', detail: 'Failed to load OData configuration' })
  } finally {
    loading.value = false
  }
}

watch([() => route.path, currentEnvironment], fetchData, { immediate: true })

// When search is active, auto-expand all matching schemas
watch(search, (q) => {
  if (q.trim()) expandAll()
})

const toggleTable = (schema, tableName) => {
  const key = tableKey(schema, tableName)
  const updated = new Set(exposedSet.value)
  if (updated.has(key)) updated.delete(key)
  else updated.add(key)
  exposedSet.value = updated
}

const saveConfig = async () => {
  saving.value = true
  try {
    const exposedTables = [...exposedSet.value].map(k => {
      const idx = k.indexOf('__')
      return { schema: k.slice(0, idx), tableName: k.slice(idx + 2) }
    })
    await api.odata.saveConfig({ exposedTables })
    toast.add({ severity: 'success', group: 'tr', summary: 'Saved', detail: 'OData configuration saved' })
  } catch (e) {
    toast.add({ severity: 'error', group: 'tr', summary: 'Error', detail: 'Failed to save OData configuration' })
  } finally {
    saving.value = false
  }
}

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text)
  toast.add({ severity: 'info', group: 'tr', summary: 'Copied', detail: 'URL copied to clipboard', life: 2000 })
}

const tableEntityUrl = (schema, tableName) => `${odataBaseUrl.value}/${tableKey(schema, tableName)}`
const exposedCount = computed(() => exposedSet.value.size)
const totalCount = computed(() => tables.value.length)
</script>

<template>
  <div class="p-6 max-w-5xl mx-auto">
    <div class="flex items-center gap-3 mb-6">
      <i class="pi pi-database text-2xl text-purple-600" />
      <div>
        <h1 class="text-2xl font-bold text-surface-800 dark:text-surface-0">
          OData 4.0
        </h1>
        <p class="text-surface-500 dark:text-surface-400 text-sm mt-1">
          Expose database tables via the OData 4.0 protocol
        </p>
      </div>
    </div>

    <!-- OData endpoint info -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      <div class="bg-surface-0 dark:bg-surface-800 rounded-lg border dark:border-surface-700 p-4">
        <div class="text-xs font-semibold text-surface-500 uppercase mb-2">Service Document</div>
        <div class="flex items-center gap-2">
          <code class="text-xs text-purple-600 dark:text-purple-400 flex-1 truncate">{{ serviceDocumentUrl }}</code>
          <Button icon="pi pi-copy" text size="small" @click="copyToClipboard(serviceDocumentUrl)" />
          <Button icon="pi pi-external-link" text size="small" as="a" :href="serviceDocumentUrl" target="_blank" />
        </div>
      </div>
      <div class="bg-surface-0 dark:bg-surface-800 rounded-lg border dark:border-surface-700 p-4">
        <div class="text-xs font-semibold text-surface-500 uppercase mb-2">Metadata ($metadata)</div>
        <div class="flex items-center gap-2">
          <code class="text-xs text-purple-600 dark:text-purple-400 flex-1 truncate">{{ metadataUrl }}</code>
          <Button icon="pi pi-copy" text size="small" @click="copyToClipboard(metadataUrl)" />
          <Button icon="pi pi-external-link" text size="small" as="a" :href="metadataUrl" target="_blank" />
        </div>
      </div>
    </div>

    <!-- OData query options cheatsheet -->
    <Message severity="info" class="mb-6" :closable="false">
      <span class="text-sm">
        Supported query options:
        <code class="mx-1">$top</code>
        <code class="mx-1">$skip</code>
        <code class="mx-1">$select</code>
        <code class="mx-1">$orderby</code>
        <code class="mx-1">$filter</code>
        <code class="mx-1">$count=true</code>
        — URL format:
        <code class="ml-1">/odata/&lt;schema&gt;__&lt;table&gt;?$top=10&$filter=name eq 'Acme'</code>
      </span>
    </Message>

    <!-- Table selection grouped by schema -->
    <div class="bg-surface-0 dark:bg-surface-800 rounded-lg border dark:border-surface-700">
      <!-- Toolbar -->
      <div class="flex flex-wrap items-center gap-3 p-4 border-b dark:border-surface-700">
        <div class="flex items-center gap-2 flex-1 min-w-0">
          <span class="font-semibold text-surface-800 dark:text-surface-0 shrink-0">Available Tables</span>
          <span class="text-surface-500 text-sm shrink-0">
            {{ exposedCount }} / {{ totalCount }} exposed
          </span>
        </div>
        <IconField class="w-56">
          <InputIcon class="pi pi-search" />
          <InputText
            v-model="search"
            placeholder="Search tables…"
            size="small"
            class="w-full"
          />
        </IconField>
        <div class="flex items-center gap-1">
          <Button
            v-tooltip.top="'Collapse all'"
            icon="pi pi-minus-circle"
            text
            size="small"
            :disabled="loading"
            @click="collapseAll"
          />
          <Button
            v-tooltip.top="'Expand all'"
            icon="pi pi-plus-circle"
            text
            size="small"
            :disabled="loading"
            @click="expandAll"
          />
        </div>
        <Button
          label="Save"
          icon="pi pi-save"
          size="small"
          :loading="saving"
          :disabled="loading"
          @click="saveConfig"
        />
      </div>

      <div v-if="loading" class="flex justify-center p-10">
        <ProgressSpinner style="width: 40px; height: 40px" stroke-width="4" />
      </div>

      <div v-else-if="tables.length === 0" class="p-8 text-center text-surface-500">
        <i class="pi pi-inbox text-4xl mb-3 block" />
        No tables found in the current environment
      </div>

      <div v-else-if="tablesBySchema.length === 0" class="p-8 text-center text-surface-500">
        <i class="pi pi-search text-4xl mb-3 block" />
        No tables match "{{ search }}"
      </div>

      <template v-else>
        <div
          v-for="([schema, schemasTables]) in tablesBySchema"
          :key="schema"
        >
          <!-- Schema header (clickable to collapse/expand) -->
          <button
            type="button"
            class="w-full px-4 py-2 bg-surface-50 dark:bg-surface-900 border-b dark:border-surface-700 flex items-center gap-2 hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors cursor-pointer"
            @click="toggleSchema(schema)"
          >
            <i
              class="pi text-xs text-surface-400 transition-transform duration-200"
              :class="isCollapsed(schema) ? 'pi-chevron-right' : 'pi-chevron-down'"
            />
            <i class="pi pi-folder text-xs text-surface-400" />
            <span class="text-xs font-semibold text-surface-500 uppercase tracking-wide">{{ schema }}</span>
            <span class="text-xs text-surface-400 ml-1">
              ({{ schemasTables.filter(t => exposedSet.has(tableKey(schema, t))).length }}/{{ schemasTables.length }})
            </span>
          </button>
          <!-- Tables in schema (collapsible) -->
          <div
            v-show="!isCollapsed(schema)"
            class="divide-y dark:divide-surface-700"
          >
            <div
              v-for="tableName in schemasTables"
              :key="tableName"
              class="flex items-center justify-between px-4 py-3 hover:bg-surface-50 dark:hover:bg-surface-700 transition-colors"
            >
              <div class="flex items-center gap-3 min-w-0">
                <i
                  class="pi pi-table text-sm"
                  :class="exposedSet.has(tableKey(schema, tableName)) ? 'text-purple-600' : 'text-surface-400'"
                />
                <span class="font-medium text-sm text-surface-800 dark:text-surface-0 truncate">{{ tableName }}</span>
              </div>
              <div class="flex items-center gap-3 shrink-0 ml-4">
                <template v-if="exposedSet.has(tableKey(schema, tableName))">
                  <div class="flex items-center gap-1">
                    <code class="text-xs text-purple-600 dark:text-purple-400 hidden sm:inline">
                      /odata/{{ tableKey(schema, tableName) }}
                    </code>
                    <Button
                      icon="pi pi-copy"
                      text
                      size="small"
                      class="hidden sm:inline-flex"
                      @click="copyToClipboard(tableEntityUrl(schema, tableName))"
                    />
                  </div>
                </template>
                <ToggleSwitch
                  :model-value="exposedSet.has(tableKey(schema, tableName))"
                  @update:model-value="toggleTable(schema, tableName)"
                />
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
